import * as THREE from 'three';
import SunCalc from 'suncalc';

// --------------------------------------------------------
// ARCHITECTURE SETUP: Force Three.js to use Z-Up 
// Google Maps WebGL uses Z-Up (+X East, +Y North, +Z Up)
// --------------------------------------------------------
THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

export class SharedScene {
  constructor() {
    this.scene = new THREE.Scene();
    
    // Group to hold our loaded stadium model
    this.stadiumGroup = new THREE.Group();
    this.scene.add(this.stadiumGroup);
    
    this._initLighting();
    this._initShadowCatcher();
  }

  _initLighting() {
    // Ambient Light
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.4); 
    this.scene.add(this.ambientLight);
    
    // Sun Directional Light
    this.sunLight = new THREE.DirectionalLight(0xffffff, 2.5);
    this.sunLight.castShadow = true;
    
    const d = 300;
    this.sunLight.shadow.camera.left = -d; 
    this.sunLight.shadow.camera.right = d;
    this.sunLight.shadow.camera.top = d; 
    this.sunLight.shadow.camera.bottom = -d;
    this.sunLight.shadow.camera.near = 0.5; 
    this.sunLight.shadow.camera.far = 3000;
    this.sunLight.shadow.bias = -0.0005;
    this.sunLight.shadow.mapSize.width = 4096; 
    this.sunLight.shadow.mapSize.height = 4096;
    
    this.scene.add(this.sunLight);
    this.scene.add(this.sunLight.target); // Ensure the light's target is added to the scene
  }

  _initShadowCatcher() {
    // Create a large ground plane
    const shadowPlaneGeo = new THREE.PlaneGeometry(1500, 1500);
    
    // Transparent shadow-catcher for overlaying shadows directly on Google Maps
    this.mapsMaterial = new THREE.ShadowMaterial({ 
      opacity: 0.6,
      depthWrite: false, // Ensures it doesn't mess with maps 3D terrain occlusion
      polygonOffset: true,
      polygonOffsetFactor: -4,
      polygonOffsetUnits: -4 
    });

    // Solid grass-like ground for Basic 3D Mode
    this.basicMaterial = new THREE.MeshStandardMaterial({ 
      color: 0x3d6e3d, 
      roughness: 1.0,
      metalness: 0.1
    });

    this.shadowPlane = new THREE.Mesh(shadowPlaneGeo, this.mapsMaterial);
    this.shadowPlane.receiveShadow = true;
    this.shadowPlane.frustumCulled = false; // CRITICAL: Prevent camera culling
    this.scene.add(this.shadowPlane);
  }

  /**
   * Adjust materials and scene properties based on the active display mode.
   * @param {string} mode - 'maps' or 'basic'
   */
  setMode(mode) {
    if (mode === 'maps') {
      this.shadowPlane.material = this.mapsMaterial;
      this.scene.background = null; // Transparent scene background to see maps underneath
    } else {
      this.shadowPlane.material = this.basicMaterial;
      const isDay = this.sunLight.position.z > 0;
      this.scene.background = new THREE.Color(isDay ? 0x87CEEB : 0x0a1128); // Dynamic startup background
    }
  }

  /**
   * Helper to get timezone offset string dynamically (e.g. "-05:00")
   */
  _getTimezoneOffsetString(date, timeZone) {
    try {
      const formatted = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset'
      }).format(date);
      
      const match = formatted.match(/GMT([+-]\d+)?(:(\d+))?$/);
      if (!match) return '-05:00';
      
      const signHour = match[1] || '+0';
      const minutes = match[3] || '00';
      
      const sign = signHour.startsWith('-') ? '-' : '+';
      const hourVal = signHour.replace(/[+-]/, '');
      
      const paddedHour = String(parseInt(hourVal)).padStart(2, '0');
      const paddedMin = String(parseInt(minutes)).padStart(2, '0');
      
      return `${sign}${paddedHour}:${paddedMin}`;
    } catch (err) {
      console.warn('Failed to parse timezone offset:', err);
      return '-05:00';
    }
  }

  /**
   * Update the directional light position based on date, time, and coordinates.
   * @returns {boolean} Whether the sun is currently above the horizon (isDay)
   */
  updateSun(lat, lng, dateStr, timeVal, timeZone = 'America/Chicago') {
    const hours = Math.floor(timeVal);
    const minutes = Math.floor((timeVal % 1) * 60);

    // Calculate dynamic timezone offset for the given date & timezone
    const baseDate = new Date(`${dateStr}T12:00:00Z`); // reference date to get offset
    const offsetStr = this._getTimezoneOffsetString(baseDate, timeZone);

    const simDate = new Date(`${dateStr}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00${offsetStr}`);
    const sunPos = SunCalc.getPosition(simDate, lat, lng);
    
    // SunCalc Azimuth: 0 is South, positive is West.
    // Three.js Maps (Z-Up): +X East, +Y North, +Z Up.
    const dist = 1500;
    const sx = -dist * Math.sin(sunPos.azimuth) * Math.cos(sunPos.altitude);
    const sy = -dist * Math.cos(sunPos.azimuth) * Math.cos(sunPos.altitude);
    const sz = dist * Math.sin(sunPos.altitude);

    this.sunLight.position.set(sx, sy, sz);
    
    // Turn sun off if below horizon
    const isDay = sz > 0;
    this.sunLight.intensity = isDay ? 2.5 : 0;

    // Adjust basic mode sky color dynamically for day/night
    if (this.shadowPlane.material === this.basicMaterial) {
      if (isDay) {
        this.scene.background.setHex(0x87CEEB); // Sky Blue
      } else {
        this.scene.background.setHex(0x0a1128); // Midnight Blue
      }
    }

    return isDay;
  }
}
